import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import SectionCard from "../shared/SectionCard";
import "../shared/chartSetup";

const buildOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      align: "start",
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        font: { size: 10 },
        padding: 14,
        usePointStyle: true,
        pointStyle: "circle",
        color: "#667085",
      },
    },
    tooltip: { mode: "index" },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      border: { display: false },
      ticks: { font: { size: 9 }, color: "#98A2B3" },
    },
    y: {
      stacked: true,
      display: false,
    },
  },
});

/**
 * "How do you acquire users?" stacked bar chart.
 * - categoryTabs  : top pill tabs (e.g. Traffic channel / Source / Referrals)
 * - timeTabs      : bottom pill buttons (e.g. 12 months / 30 days / 7 days)
 *
 * @param {string}     [title="How do you acquire users?"]
 * @param {string[]}   categoryTabs         Top filter tabs
 * @param {string}     [defaultCategory]    Initially active category tab
 * @param {string[]}   timeTabs             Bottom time-range tabs
 * @param {string}     [defaultTime]        Initially active time tab
 * @param {object}     chartData            Chart.js bar data object
 * @param {object}     [chartOptions]       Overrides default chart options
 * @param {number}     [chartHeight=180]    Height of the chart canvas in px
 * @param {Function}   [onCategoryChange]   Called with the new category string
 * @param {Function}   [onTimeChange]       Called with the new time string
 */
const AcquireUsersChart = ({
  title = "How do you acquire users?",
  categoryTabs = [],
  defaultCategory,
  timeTabs = [],
  defaultTime,
  chartData,
  chartOptions,
  chartHeight = 180,
  onCategoryChange,
  onTimeChange,
}) => {
  const [activeCategory, setActiveCategory] = useState(
    defaultCategory ?? categoryTabs[0],
  );
  const [activeTime, setActiveTime] = useState(defaultTime ?? timeTabs[0]);

  const handleCategory = (t) => {
    setActiveCategory(t);
    onCategoryChange?.(t);
  };
  const handleTime = (t) => {
    setActiveTime(t);
    onTimeChange?.(t);
  };

  return (
    <SectionCard title={title}>
      {/* ── Category tabs ── */}
      {categoryTabs.length > 0 && (
        <div className="d-flex gap-1 flex-wrap mb-3">
          {categoryTabs.map((t) => {
            const isActive = t === activeCategory;
            return (
              <button
                key={t}
                onClick={() => handleCategory(t)}
                style={{
                  background: isActive ? "#F9F5FF" : "transparent",
                  color: isActive ? "#6941C6" : "#667085",
                  border: `1px solid ${isActive ? "#D6BBFB" : "transparent"}`,
                  borderRadius: 20,
                  padding: "4px 14px",
                  fontSize: "0.75rem",
                  fontWeight: isActive ? 600 : 400,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Stacked bar chart ── */}
      <div style={{ height: chartHeight }}>
        <Bar
          data={chartData ?? { labels: [], datasets: [] }}
          options={chartOptions ?? buildOptions()}
        />
      </div>

      {/* ── Time-range tabs ── */}
      {timeTabs.length > 0 && (
        <div className="d-flex gap-2 justify-content-center mt-3">
          {timeTabs.map((t) => {
            const isActive = t === activeTime;
            return (
              <button
                key={t}
                onClick={() => handleTime(t)}
                style={{
                  background: isActive ? "#1D2939" : "#fff",
                  color: isActive ? "#fff" : "#344054",
                  border: "1px solid #E4E7EC",
                  borderRadius: 20,
                  padding: "6px 18px",
                  fontSize: "0.8rem",
                  fontWeight: isActive ? 600 : 400,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};

export default AcquireUsersChart;
